import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import RxTxLineChart from '@bumble/app/components/RxTxLineChart';
import TimeRange from '@bumble/app/components/TimeRange';
import { TIME_BUTTONS } from '@bumble/app/constants';
import { useApi } from '@bumble/app/hooks/use-api';
import { Box, Card, CardContent, Grid, Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DateTime, Duration, DurationLikeObject } from 'luxon';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

interface DataCards {
  appservices: string;
  endpoints: string;
  service_nodes: string;
  endpoint_nodes: string;
  user_agents: string;
}

const dataCards: DataCards = {
  appservices: 'App Services',
  endpoints: 'Endpoints',
  service_nodes: 'Service Nodes',
  endpoint_nodes: 'Endpoint Nodes',
  user_agents: 'User Agents',
} as const;
export const Dashboard = () => {
  const api = useApi();

  const [timeRange, setTimeRange] = useState<DurationLikeObject>(TIME_BUTTONS[0].value);

  const handleTimeChange = (e: React.MouseEvent<HTMLElement>, val: DurationLikeObject) => {
    if (val) setTimeRange(val);
  };

  const {
    isLoading: summaryIsLoading,
    data: summaryData,
    // refetch: epRefetch,
  } = useQuery(['accountSummary'], api.getAccountSummaryApiV1GetAccountSummaryGet.bind(null, {}), {
    enabled: true,
  });

  const [epIds, setEpIds] = useState<string[]>([]);
  const {
    isLoading: epIsLoading,
    data: epData,
    // refetch: epRefetch,
  } = useQuery(
    ['endpointsdash'],
    api.listEndpointDashboardApiV1ListEndpointDashboardGet.bind(null, {}),
    { enabled: true },
  );
  const {
    // isLoading: epStatsIsLoading,
    data: epStatsData,
    refetch: epStatsRefetch,
    isRefetching: epStatsIsRefetching,
  } = useQuery(
    ['epNetStats'],
    api.getNetStatsApiV1GetNetStatsGet.bind(null, {
      ids: epIds.join(),
      start_time: DateTime.now().minus(timeRange).toISO(),
      end_time: DateTime.now().toISO(),
    }),
    { enabled: false },
  );
  useEffect(() => {
    if (epData && epData?.data) {
      setEpIds(epData?.data.map((ep) => ep.ep_id));
    }
  }, [epData]);

  const [apIds, setApIds] = useState<string[]>([]);
  const {
    isLoading: apIsLoading,
    data: apData,
    // refetch: apRefetch,
  } = useQuery(['appServices'], api.listAppServiceApiV1ListAppServiceGet.bind(null, {}), {
    enabled: true,
  });
  const {
    // isLoading: apStatsIsLoading,
    data: apStatsData,
    refetch: apStatsRefetch,
    isRefetching: apStatsIsRefetching,
  } = useQuery(
    ['apNetStats'],
    api.getNetStatsApiV1GetNetStatsGet.bind(null, {
      ids: apIds.join(),
      start_time: DateTime.now().minus(timeRange).toISO(),
      end_time: DateTime.now().toISO(),
    }),
    { enabled: false },
  );

  useEffect(() => {
    // setApIds(['app-1,app-2,app-3,app-4,app-5,app-6']);
    if (apData && apData?.data) {
      setApIds(apData?.data.map((ap) => ap.app_service_id));
    }
  }, [apData]);

  useEffect(() => {
    if (apIds.length > 0) apStatsRefetch();
    if (epIds.length > 0) epStatsRefetch();
  }, [timeRange, apStatsRefetch, epStatsRefetch, apIds.length, epIds.length]);

  const handleRefreshClick = () => {
    if (apIds.length > 0) apStatsRefetch();
    if (epIds.length > 0) epStatsRefetch();
    toast.success('Refreshed chart data.');
  };

  const selectedHours = parseInt(Duration.fromObject(timeRange).toFormat('h'), 10);

  return (
    <PageContent>
      <Box sx={{ mb: 10, py: 5 }}>
        <Grid container spacing={5}>
          {Object.entries(dataCards).map(([key, value], i) => (
            <Grid item xs={Object.entries(dataCards).length === i + 1 ? 12 : 6} md={4} lg key={key}>
              <Card elevation={0}>
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: { xs: 5, md: 10, lg: 12 },
                  }}
                >
                  <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }} color="primary">
                    {summaryIsLoading ? (
                      <Skeleton variant="rectangular" width={20} height={36} />
                    ) : (
                      (summaryData && summaryData.data[key as keyof typeof summaryData.data]) || 0
                    )}
                  </Typography>
                  <Typography variant="subtitle1">{value}</Typography>
                </CardContent>
                <div />
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
      {epStatsData?.data || apStatsData?.data ? (
        <TimeRange
          timeValues={TIME_BUTTONS}
          selectedTime={timeRange}
          onTimeChange={handleTimeChange}
          onRefresh={handleRefreshClick}
        />
      ) : (
        <div />
      )}
      {epIsLoading ? (
        <Loading />
      ) : (
        epIds.length > 0 &&
        epStatsData?.data && (
          <>
            <Typography variant="h4" sx={{ mb: 5 }}>
              Endpoints
            </Typography>
            <Grid container spacing={5}>
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ mb: 10 }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ mb: 5, textAlign: 'center' }}>
                      Transmit (bytes/sec)
                    </Typography>
                    <RxTxLineChart
                      loading={epStatsIsRefetching}
                      selector="tx.bytes"
                      data={epStatsData?.data}
                      hours={selectedHours}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ mb: 10 }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ mb: 5, textAlign: 'center' }}>
                      Receive (bytes/sec)
                    </Typography>
                    <RxTxLineChart
                      loading={epStatsIsRefetching}
                      selector="rx.bytes"
                      data={epStatsData?.data}
                      hours={selectedHours}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )
      )}
      {apIsLoading ? (
        <Loading />
      ) : (
        apIds.length > 0 &&
        apStatsData?.data && (
          <>
            <Typography variant="h4" sx={{ mb: 5 }}>
              App Services
            </Typography>
            <Grid container spacing={5}>
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ mb: 10 }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ mb: 5, textAlign: 'center' }}>
                      Transmit (bytes/sec)
                    </Typography>
                    <RxTxLineChart
                      loading={apStatsIsRefetching}
                      selector="tx.bytes"
                      data={apStatsData?.data}
                      hours={selectedHours}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ mb: 10 }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ mb: 5, textAlign: 'center' }}>
                      Receive (bytes/sec)
                    </Typography>
                    <RxTxLineChart
                      loading={apStatsIsRefetching}
                      selector="rx.bytes"
                      data={apStatsData?.data}
                      hours={selectedHours}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )
      )}
    </PageContent>
  );
};
